import React, { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { X } from "lucide-react";
import { useNavigate } from "react-router-dom";

import { withLocale } from "../utils/locale";
import "./DideokAssistant.css";

const DAY_MS = 24 * 60 * 60 * 1000;
const EXPIRING_AFTER_DAYS = 59;
const STALE_AFTER_DAYS = 1;
const AUTO_COLLAPSE_MS = 9000;

const DIDEOK_MESSAGES = {
  guest: [
    "오늘도 새로운 서버가 잔뜩이다 꽥!\n마음에 드는 곳을 찾아보라 꽥!",
    "새로운 인연이 기다리고 있을지도 모른다 꽥!\n서버 카드를 살펴보라 꽥!",
    "서버를 운영하고 있나 꽥?\n로그인하면 홍보 상태를 알려주겠다 꽥!",
    "궁금한 게 있다면 나를 눌러보라 꽥!\n디덕이가 도와주겠다 꽥!",
  ],
  noServer: [
    "아직 서버를 추가하지 않았다 꽥!\n서버를 추가하러 가보라 꽥!!",
    "홍보할 서버가 보이지 않는다 꽥!\n지금 바로 서버를 등록해보라 꽥!",
    "서버를 혼자만 알고 있기엔 아깝다 꽥!\n디스파티에 알려보라 꽥!",
    "새로운 멤버를 찾고 있지 않나 꽥?\n서버를 등록하면 내가 도와주겠다 꽥!",
    "서버 등록은 생각보다 금방 끝난다 꽥!\n한번 시작해보라 꽥!",
    "서버가 북적북적해지고 싶다면 꽥!\n일단 디스파티에 추가해보라 꽥!",
  ],
  neverReloaded: [
    "서버 등록은 끝났지만 아직 갱신 전이다 꽥!\n디스코드에서 /party를 실행해보라 꽥!",
    "등록한 서버가 아직 피드에 보이지 않는다 꽥!\n/party로 첫 갱신을 해보라 꽥!",
    "마지막 한 단계가 남았다 꽥!\n서버에서 /party를 입력해보라 꽥!",
  ],
  expiring: [
    "서버가 곧 목록에서 사라질 수 있다 꽥!\n지금 /party로 갱신하라 꽥!!",
    "서버가 잠들기 직전이다 꽥!\n얼른 갱신해서 깨워보라 꽥!",
    "삭제까지 시간이 얼마 남지 않았다 꽥!\n지금 서버를 갱신해보라 꽥!",
    "이대로 두면 서버가 사라질 수도 있다 꽥!\n/party를 사용할 시간이다 꽥!",
  ],
  stale: [
    "서버가 목록 아래로 내려가고 있다 꽥!\n갱신해서 다시 올려보라 꽥!",
    "마지막 갱신으로부터 시간이 좀 지났다 꽥!\n/party를 사용할 시간이다 꽥!",
    "서버가 잠들려고 한다 꽥!\n얼른 갱신해서 깨워보라 꽥!",
    "새로운 멤버에게 서버를 보여주고 싶다면 꽥!\n지금 갱신해보라 꽥!",
  ],
  active: [
    "등록한 서버가 잘 노출되고 있다 꽥!\n오늘도 상태를 확인해보라 꽥!",
    "서버 홍보가 열심히 진행 중이다 꽥!\n새로운 멤버를 기다려보자 꽥!",
    "서버 카드가 아주 잘 보인다 꽥!\n설명을 더 멋지게 꾸며봐도 좋겠다 꽥!",
    "사람들이 서버를 구경하고 있다 꽥!\n최신 정보로 관리해보라 꽥!",
    "홍보는 꾸준함이 중요하다 꽥!\n서버를 한번 점검해보라 꽥!",
    "오늘은 어떤 사람들이 찾아올까 꽥?\n서버 상태를 확인해보자 꽥!",
  ],
  unavailable: [
    "서버 상태를 잠시 확인하지 못했다 꽥!\n조금 뒤에 다시 불러보라 꽥!",
    "상태 확인이 잠깐 늦어지고 있다 꽥!\n서버 관리에서 직접 확인해보라 꽥!",
  ],
};

const STATE_ACTIONS = {
  guest: { label: "로그인", path: "/profile" },
  noServer: { label: "서버 추가", path: "/profile" },
  neverReloaded: { label: "내 서버 관리", path: "/profile" },
  expiring: { label: "지금 확인", path: "/profile" },
  stale: { label: "내 서버 관리", path: "/profile" },
  active: { label: "내 서버 관리", path: "/profile" },
  unavailable: { label: "상태 확인", path: "/profile" },
};

function daysSince(value) {
  const timestamp = new Date(value).getTime();
  if (!Number.isFinite(timestamp)) return null;
  return Math.max(0, Math.floor((Date.now() - timestamp) / DAY_MS));
}

function resolveState(isLoggedIn, myServers, loadFailed) {
  if (!isLoggedIn) return "guest";
  if (loadFailed) return "unavailable";
  if (myServers.length === 0) return "noServer";
  if (myServers.some((server) => !server.reloaded_at)) return "neverReloaded";

  const oldestReloadDays = myServers.reduce((oldest, server) => {
    const elapsed = daysSince(server.reloaded_at);
    return elapsed === null ? oldest : Math.max(oldest, elapsed);
  }, 0);

  if (oldestReloadDays >= EXPIRING_AFTER_DAYS) return "expiring";
  if (oldestReloadDays >= STALE_AFTER_DAYS) return "stale";
  return "active";
}

export default function DideokAssistant({ isLoggedIn, loadingAuth, locale = "ko", hidden = false }) {
  const navigate = useNavigate();
  const prefersReducedMotion = useReducedMotion();
  const [myServers, setMyServers] = useState([]);
  const [loadingServers, setLoadingServers] = useState(false);
  const [loadFailed, setLoadFailed] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [messageSeed] = useState(() => Math.random());

  useEffect(() => {
    if (locale !== "ko" || loadingAuth) return undefined;
    if (!isLoggedIn) {
      setMyServers([]);
      setLoadFailed(false);
      setLoadingServers(false);
      return undefined;
    }

    let active = true;

    const loadMyServers = () => {
      setLoadingServers(true);
      fetch("https://disparty.com/api/servers/mine", { credentials: "include" })
        .then((response) => (response.ok ? response.json() : Promise.reject(response)))
        .then((data) => {
          if (!active) return;
          setMyServers(Array.isArray(data) ? data : []);
          setLoadFailed(false);
        })
        .catch(() => {
          if (!active) return;
          setLoadFailed(true);
        })
        .finally(() => {
          if (active) setLoadingServers(false);
        });
    };

    loadMyServers();
    window.addEventListener("focus", loadMyServers);

    return () => {
      active = false;
      window.removeEventListener("focus", loadMyServers);
    };
  }, [isLoggedIn, loadingAuth, locale]);

  const state = useMemo(
    () => resolveState(isLoggedIn, myServers, loadFailed),
    [isLoggedIn, loadFailed, myServers]
  );

  const message = useMemo(() => {
    const messages = DIDEOK_MESSAGES[state] || DIDEOK_MESSAGES.guest;
    return messages[Math.floor(messageSeed * messages.length) % messages.length];
  }, [messageSeed, state]);

  useEffect(() => {
    if (loadingAuth || loadingServers || locale !== "ko") return undefined;

    setIsOpen(true);
    const timer = window.setTimeout(() => setIsOpen(false), AUTO_COLLAPSE_MS);
    return () => window.clearTimeout(timer);
  }, [loadingAuth, loadingServers, locale, state]);

  if (typeof document === "undefined" || locale !== "ko" || loadingAuth || loadingServers || hidden) {
    return null;
  }

  const action = STATE_ACTIONS[state] || STATE_ACTIONS.guest;
  const goToAction = () => {
    setIsOpen(false);
    navigate(withLocale(action.path, locale));
  };

  return createPortal(
    <aside className="dideok-assistant" aria-label="디덕이 안내">
      <AnimatePresence initial={false}>
        {isOpen && (
          <motion.section
            key={`${state}-${message}`}
            className="dideok-assistant__bubble"
            role="status"
            aria-live="polite"
            initial={prefersReducedMotion ? false : { opacity: 0, x: 12, y: 8, scale: 0.98 }}
            animate={{ opacity: 1, x: 0, y: 0, scale: 1 }}
            exit={prefersReducedMotion ? { opacity: 0 } : { opacity: 0, x: 8, y: 4, scale: 0.98 }}
            transition={{ duration: prefersReducedMotion ? 0 : 0.18, ease: [0.2, 0.8, 0.2, 1] }}
          >
            <button
              type="button"
              className="dideok-assistant__close"
              onClick={() => setIsOpen(false)}
              aria-label="디덕이 말풍선 닫기"
              title="닫기"
            >
              <X aria-hidden="true" />
            </button>
            <p>{message}</p>
            <button type="button" className="dideok-assistant__action" onClick={goToAction}>
              {action.label}
            </button>
          </motion.section>
        )}
      </AnimatePresence>

      <motion.button
        type="button"
        className="dideok-assistant__mascot"
        onClick={() => setIsOpen((open) => !open)}
        aria-label={isOpen ? "디덕이 말풍선 접기" : "디덕이 안내 열기"}
        aria-expanded={isOpen}
        title={isOpen ? "안내 접기" : "디덕이 안내 열기"}
        initial={prefersReducedMotion ? false : { opacity: 0, y: 12, scale: 0.92 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        whileHover={prefersReducedMotion ? undefined : { y: -3 }}
        whileTap={prefersReducedMotion ? undefined : { scale: 0.94 }}
        transition={{ duration: prefersReducedMotion ? 0 : 0.2, ease: "easeOut" }}
      >
        <img src="/dideok-assistant.png?v=20260805" alt="" draggable="false" />
      </motion.button>
    </aside>,
    document.body
  );
}
